import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, X, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UsernameOnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

export function UsernameOnboardingModal({ open, onComplete }: UsernameOnboardingModalProps) {
  const [username, setUsername] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);

  // Debounced availability check
  useEffect(() => {
    const trimmed = username.trim();
    
    // Reset states
    setIsAvailable(null);
    setFormatError(null);
    
    if (!trimmed) return;
    
    // Validate format first
    if (trimmed.length < 3) {
      setFormatError('Username must be at least 3 characters');
      return;
    }
    
    if (trimmed.length > 20) {
      setFormatError('Username must be 20 characters or less');
      return;
    }
    
    if (!USERNAME_REGEX.test(trimmed)) {
      setFormatError('Only letters, numbers, and underscores allowed');
      return;
    }
    
    // Check availability after format is valid
    const timeout = setTimeout(async () => {
      setIsChecking(true);
      try {
        const { data, error } = await supabase.rpc('check_username_available', {
          p_username: trimmed,
        });
        
        if (error) throw error;
        setIsAvailable(data as boolean);
      } catch (err) {
        console.error('Error checking username:', err);
        setIsAvailable(null);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [username]);

  const handleSave = async () => {
    const trimmed = username.trim();
    
    if (!trimmed || formatError || !isAvailable) {
      return;
    }

    setIsSaving(true);
    
    try {
      const { data, error } = await supabase.rpc('set_username', {
        p_username: trimmed,
      });

      if (error) throw error;

      const result = data as { success: boolean; message: string };
      
      if (result.success) {
        toast.success('Username saved successfully!');
        onComplete();
      } else {
        toast.error(result.message || 'Failed to save username');
      }
    } catch (err) {
      console.error('Error saving username:', err);
      toast.error('Failed to save username. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusIcon = () => {
    if (isChecking) {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    if (formatError) {
      return <X className="h-4 w-4 text-destructive" />;
    }
    if (isAvailable === true) {
      return <Check className="h-4 w-4 text-green-500" />;
    }
    if (isAvailable === false) {
      return <X className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  const getStatusMessage = () => {
    if (formatError) {
      return <span className="text-destructive text-xs">{formatError}</span>;
    }
    if (isChecking) {
      return <span className="text-muted-foreground text-xs">Checking availability...</span>;
    }
    if (isAvailable === true) {
      return <span className="text-green-500 text-xs">Username is available!</span>;
    }
    if (isAvailable === false) {
      return <span className="text-destructive text-xs">This username is already taken</span>;
    }
    return null;
  };

  const isValid = !formatError && isAvailable === true && !isChecking;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-xl">Choose your username</DialogTitle>
          <DialogDescription>
            This will be your identity inside WinWGo. Choose wisely!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="relative">
              <Input
                id="username"
                placeholder="e.g. football_fan_99"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                className="pr-10"
                disabled={isSaving}
                autoFocus
                autoComplete="off"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {getStatusIcon()}
              </div>
            </div>
            <div className="min-h-[20px]">
              {getStatusMessage()}
            </div>
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>• 3-20 characters</p>
            <p>• Letters, numbers, and underscores only</p>
            <p>• No spaces or special characters</p>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={!isValid || isSaving}
          className="w-full h-11"
        >
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save and Continue
        </Button>
      </DialogContent>
    </Dialog>
  );
}
